/**
 * LaunchDarkly alerts (`web-alert-banner` flag).
 *
 * The flag's value is JSON: a single alert object or an array of them. Web and
 * native both read it and render each eligible alert as one of two display
 * types, chosen per entry with `displayType`.
 *
 * ## Banner (`"displayType": "banner"`, the default)
 *
 * - Colored bar across the top of the app for outages and critical alerts.
 * - Spans all pages and pushes content down; it never overlays content.
 * - NOT dismissible. It shows for as long as it is eligible (status on, inside
 *   its date window, audience matches).
 * - Renders `message` and the optional `link`. `title` is ignored.
 *
 * ## Message block (`"displayType": "messageBlock"`)
 *
 * - Sits at the top of the page, directly below any banners.
 * - Spans all pages and overlays content.
 * - Dismissible with an ×. Once dismissed it is never shown again for that user
 *   on that device (dismissals are stored per user id, keyed by alert `id`).
 * - Renders an optional bold `title`, the `message` body, the optional `link`,
 *   and the ×. Toasts are message blocks with a timeout.
 *
 * A missing or unrecognized `displayType` is treated as `banner`, so existing
 * flag entries keep their current behavior. `title` is only rendered by message
 * blocks.
 *
 * ## Shared rules (both types)
 *
 * - `startDate` / `endDate` are epoch **seconds**; the alert shows only while
 *   `startDate <= now <= endDate`.
 * - `status: false` hides the alert.
 * - `alertType` gates the audience: the two compliance types show only to users
 *   with legacy compliance enabled, no XP compliance plans, and the matching
 *   rollout (phase segment via RULE_MATCH, chain-wide via FALLTHROUGH). Any
 *   other or missing `alertType` shows only to leaders and operators.
 * - `id` should be set and unique; it is the dismissal key for message blocks.
 *   When omitted, an id is derived from the dates, message, and link URL, so
 *   editing any of those makes a dismissed message block show again.
 * - A malformed optional field (`title`, `link`, `id`, `alertType`,
 *   `displayType`, or a non-string `es`) is ignored and the entry still shows;
 *   e.g. a `link` without valid `text` or a non-empty `url` is dropped, an
 *   invalid `id` is replaced by the derived one, and an unknown `displayType`
 *   falls back to `banner`. Only bad required fields (`startDate`, `endDate`,
 *   `status`, `message.en`) drop the entry.
 * - The flag's OFF variation (`{ "message": null, "status": false }`) and any
 *   malformed entries are ignored.
 *
 * ## Example flag value
 *
 * ```json
 * [
 *   {
 *     "id": "outage-2026-11-03",
 *     "displayType": "banner",
 *     "startDate": 1793664000,
 *     "endDate": 1793750400,
 *     "status": true,
 *     "message": {
 *       "en": "Pathway is experiencing an outage. We are working on a fix.",
 *       "es": "Pathway está experimentando una interrupción. Estamos trabajando en una solución."
 *     },
 *     "link": {
 *       "text": { "en": "Status page", "es": "Página de estado" },
 *       "url": "https://status.example.com"
 *     }
 *   },
 *   {
 *     "id": "new-reports-2026-11",
 *     "displayType": "messageBlock",
 *     "startDate": 1793664000,
 *     "endDate": 1796256000,
 *     "status": true,
 *     "title": { "en": "New reports", "es": "Nuevos informes" },
 *     "message": {
 *       "en": "Reports now include completion trends.",
 *       "es": "Los informes ahora incluyen tendencias de finalización."
 *     },
 *     "link": {
 *       "text": { "en": "View reports", "es": "Ver informes" },
 *       "url": "/reports"
 *     }
 *   }
 * ]
 * ```
 */

export const AlertDisplayType = {
  Banner: 'banner',
  MessageBlock: 'messageBlock',
} as const;

export type AlertDisplayType =
  (typeof AlertDisplayType)[keyof typeof AlertDisplayType];

export type AlertType =
  | 'CompliancePlanCompletionNoticePhased'
  | 'CompliancePlanCompletionNoticeChainWide'
  // Keeps literal autocomplete while accepting any string.
  | (string & Record<never, never>);

export interface LanguageObject {
  en: string;
  es?: string;
}

export interface LaunchDarklyAlert {
  /** Epoch seconds. */
  startDate: number;
  /** Epoch seconds. */
  endDate: number;
  message: LanguageObject;
  /** Bold heading; rendered by message blocks only. */
  title?: LanguageObject;
  link?: {
    text: LanguageObject;
    url: string;
  };
  status: boolean;
  id?: string;
  alertType?: AlertType;
  /** Missing or unknown values mean `banner`. */
  displayType?: AlertDisplayType;
}

export type LaunchDarklyAlertFlag =
  LaunchDarklyAlert | LaunchDarklyAlert[] | undefined;
